import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Notice } from "../components/ui/Notice";
import { Panel, SectionTitle, Toolbar } from "../components/ui/Panel";
import { discordUnbind } from "../lib/api";
import { DiscordBindFlow } from "../components/DiscordBindFlow";
import { useT } from "../lib/i18n";
import { currentToken, isDiscordTicket } from "../lib/session";

/**
 * Discord 绑定（notify-hub V2，Luis 2026-09-29 的接入规范）。
 *
 * **为什么要有这一页**：Discord 登录链接不是我们发的，是绑定成功后 notify-hub 通过 Discord 私信
 * 推给本人的；而且绑定页地址不能由前端自己拼（规范 §9.1 明确废弃），只能由后端持 API Key 调
 * `bind/link` 换一条短链。所以「第一次用 Discord 登录」必须先有人在这里绑一次——也因此
 * 账号密码那条登录路必须留着，否则谁都进不来做第一次绑定。
 *
 * 绑定界面是我们自己画的（components/DiscordBindFlow，服务端代调 notify-hub 的绑定会话接口）：
 * 显示邀请链接和要发给机器人的命令，轮询到 `bound` 就原位显示绑定成功，同时服务端记下已绑。
 */
export function DiscordBindPage({ onToast, self }: { onToast: (s: string) => void; self: string | null }) {
  const t = useT();
  /** 第几次开始绑定；换一个数就重挂 DiscordBindFlow，重新拿绑定码。0 = 还没开始 */
  const [run, setRun] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * 用 Discord 登录进来的会话，票里的 user_id 带 `discord:` 前缀，不是当初绑定用的业务 id；
   * 拿它去生成绑定码 / 解绑，notify-hub 会当成另一个人。所以这种会话只告诉他已经绑过，不给按钮。
   */
  const viaDiscord = isDiscordTicket(currentToken());

  const unbind = async () => {
    setBusy(true); setErr(null);
    try { await discordUnbind(); setRun(0); onToast(t("dc.unbound")); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Toolbar left={<span className="text-xs text-ink-placeholder">{t("dc.note")}</span>} />
      {err && <div className="mb-3"><Notice tone="danger">{err}</Notice></div>}
      <Panel>
        <SectionTitle
          extra={viaDiscord ? undefined : (
            <span className="flex gap-2">
              <Button size="sm" tone="primary" disabled={busy} onClick={() => setRun((n) => n + 1)}>{t("dc.bind")}</Button>
              <Button size="sm" tone="danger" disabled={busy} onClick={() => void unbind()}>{t("dc.unbind")}</Button>
            </span>
          )}
        >
          {t("nav.discord-bind")}
        </SectionTitle>
        <p className="text-sm leading-relaxed text-ink-regular">{t("dc.who", { who: self ?? "—" })}</p>
        {viaDiscord ? (
          <p className="mt-3 text-sm leading-relaxed text-success-800">{t("dc.viaDiscord")}</p>
        ) : run > 0 ? (
          <div className="mt-5 max-w-[460px]">
            <DiscordBindFlow key={run} onBound={(name) => onToast(name ? t("bindf.boundAs", { name }) : t("bindf.bound"))} />
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
